import type { AuthStyle, ProviderModel } from '@ai-studio/shared';
import { create } from 'zustand';

import { ApiError, api } from '../lib/api.js';

/**
 * Providers and their keys, shared by the Providers panel and the roster.
 *
 * Both surfaces need the same list, and they must agree: the key a member is
 * pointed at in the roster has to be one the Providers panel just created. Two
 * independent fetches would let those drift, so there is one store and both
 * read from it.
 */

export interface ProviderKey {
  readonly id: number;
  readonly provider: string;
  readonly label: string;
  /** Non-reversible; the key itself is never sent to the browser. */
  readonly fingerprint: string;
  readonly createdAt: string;
  readonly lastUsedAt: string | null;
  readonly lastStatus: string | null;
  readonly limits: {
    readonly rpm: number | null;
    readonly inputTokensPerMinute: number | null;
    readonly outputTokensPerMinute: number | null;
    readonly maxConcurrentCalls: number | null;
  };
  readonly usage?: {
    readonly callsInWindow: number;
    readonly inputTokensInWindow: number;
    readonly outputTokensInWindow: number;
    readonly inFlight: number;
    readonly cooldownRemainingMs: number;
  };
}

export interface ProviderEntry {
  readonly id: string;
  readonly label: string;
  /** Environment variable that can supply a key, for catalogued providers. */
  readonly envKey: string | null;
  readonly notes: string | null;
  readonly custom: boolean;
  /** How this provider authenticates. Local CLIs do not take a key. */
  readonly auth: AuthStyle;
  readonly chatBaseUrl: string;
  readonly models: readonly ProviderModel[];
  readonly keys: readonly ProviderKey[];
  /** False for machine logins: Claude CLI, Codex, Antigravity CLI and Grok CLI. */
  readonly acceptsApiKey: boolean;
  /** False when the endpoint works with no key at all. */
  readonly requiresCredential: boolean;
  /** For Antigravity CLI: whether to run with AGY_ADC_AUTH=true. */
  readonly adcAuth?: boolean;
}

export interface TestResult {
  readonly ok: boolean;
  readonly message: string;
}

interface ProviderState {
  providers: ProviderEntry[];
  error: string | null;
  loading: boolean;

  load: () => Promise<void>;
  addKey: (
    provider: string,
    input: { secret: string; label?: string; model?: string },
  ) => Promise<TestResult | null>;
  removeKey: (keyId: number) => Promise<boolean>;
  setKeyLimits: (keyId: number, limits: ProviderKey['limits']) => Promise<boolean>;
  setAntigravityAdcAuth: (adcAuth: boolean) => Promise<boolean>;
  /** Point the local model provider at a server address; null restores the default. */
  setLocalBaseUrl: (baseUrl: string | null) => Promise<boolean>;
  loadStatus: () => Promise<void>;

  /** Keys for one provider, oldest first. */
  keysFor: (providerId: string | null) => readonly ProviderKey[];
  /** Model ids to offer for one provider. */
  modelsFor: (providerId: string | null) => readonly string[];
}

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError || error instanceof Error ? error.message : fallback;
}

export const useProviders = create<ProviderState>((set, get) => ({
  providers: [],
  error: null,
  loading: false,

  load: async () => {
    set({ loading: true, error: null });
    try {
      const result = await api.get<{ providers: ProviderEntry[] }>('/agents/providers');
      set({ providers: result.providers, loading: false });
      // Gateways that publish a catalog (Logfare, Pollinations) and the local runtime
      // (the models pulled into it): replace the curated suggestions with the live
      // list. Failure keeps the suggestions.
      for (const id of ['logfare', 'pollinations', 'local']) {
        if (!result.providers.some((p) => p.id === id)) continue;
        void api
          .get<{
            live: boolean;
            models: Array<{ id: string; label: string; tools: boolean | null }>;
          }>(`/agents/providers/${id}/models`)
          .then((list) => {
            if (!list.live || list.models.length === 0) return;
            const models = list.models.map((m) => ({
              id: m.id,
              label: m.tools === false ? `${m.label} (no tools)` : m.label,
            }));
            set((state) => ({
              providers: state.providers.map((p) => (p.id === id ? { ...p, models } : p)),
            }));
          })
          .catch(() => undefined);
      }
    } catch (error) {
      set({ loading: false, error: message(error, 'Could not load providers.') });
    }
  },

  addKey: async (provider, input) => {
    try {
      const result = await api.post<{ test: TestResult | null }>(
        `/agents/providers/${encodeURIComponent(provider)}/keys`,
        input,
      );
      await get().load();
      set({ error: null });
      return result.test;
    } catch (error) {
      set({ error: message(error, 'Could not save that key.') });
      throw error;
    }
  },

  removeKey: async (keyId) => {
    try {
      await api.del(`/agents/keys/${keyId}`);
      await get().load();
      return true;
    } catch (error) {
      set({ error: message(error, 'Could not remove that key.') });
      return false;
    }
  },

  setKeyLimits: async (keyId, limits) => {
    try {
      await api.patch(`/agents/keys/${keyId}`, limits);
      await get().loadStatus();
      return true;
    } catch (error) {
      set({ error: message(error, 'Could not update key limits.') });
      return false;
    }
  },

  setAntigravityAdcAuth: async (adcAuth) => {
    try {
      await api.patch('/agents/providers/antigravity/settings', { adcAuth });
      set((state) => ({
        error: null,
        providers: state.providers.map((p) =>
          p.id === 'antigravity' ? { ...p, adcAuth } : p,
        ),
      }));
      return true;
    } catch (error) {
      set({ error: message(error, 'Could not update Antigravity ADC auth setting.') });
      return false;
    }
  },

  setLocalBaseUrl: async (baseUrl) => {
    try {
      await api.patch('/agents/providers/local/settings', { baseUrl });
      set({ error: null });
      await get().load();
      return true;
    } catch (error) {
      set({ error: message(error, 'Could not update the local model address.') });
      return false;
    }
  },

  loadStatus: async () => {
    try {
      const result = await api.get<{ keys: Array<ProviderKey & { usage: ProviderKey['usage'] }> }>(
        '/agents/keys/status',
      );
      const byId = new Map(result.keys.map((key) => [key.id, key]));
      set((state) => ({
        providers: state.providers.map((provider) => ({
          ...provider,
          keys: provider.keys.map((key) => byId.get(key.id) ?? key),
        })),
      }));
    } catch (error) {
      set({ error: message(error, 'Could not load key status.') });
    }
  },

  keysFor: (providerId) => {
    if (!providerId) return [];
    return get().providers.find((p) => p.id === providerId)?.keys ?? [];
  },

  modelsFor: (providerId) => {
    if (!providerId) return [];
    return (
      get()
        .providers.find((p) => p.id === providerId)
        ?.models.map((m) => m.id) ?? []
    );
  },
}));
